import React, { useCallback, useEffect } from "react";
import { Field, Form } from "formik";
import { Button, Popup, Grid } from "semantic-ui-react";
import Select from "components/Form/Select";
import GranteeSummary from "./GranteeSummary";
import moment from "moment";
import CustomInputTextComponent from "components/Form/CustomInputTextComponent";
import CustomDatePickerComponent from "components/Form/CustomDatePickerComponent";
import RadioButtons from "components/Form/RadioButtons";
import InputPhone from "components/Form/InputPhone";
import debounce from "lodash.debounce";

const GranteeForm = ({
	values,
	setFieldValue,
	errors,
	specialRegimeOptions,
	contactComments,
	foundGrantee,
	initFoundGrantee,
	searchByMatricule,
	formIsDisabled,
	setRelation,
	setTutor,
}) => {
	useEffect(() => {
		setTutor(values.tutor);
	}, [values.tutor]);

	const findSpecialRegime = useCallback(
		(healthFundId) => specialRegimeOptions.find((regime) => regime.geslab_id === healthFundId)?.id || null,
		[specialRegimeOptions]
	);

	const handleKeyUp = useCallback(
		debounce((val, setFieldValue) => {
			if (val?.length === 8 || val?.length === 13) {
				const rawDate = val.substring(0, 8);
				const birth_date = moment(rawDate, "YYYYMMDD", true); // parsing strict
				if (!birth_date.isValid()) return;
				if (birth_date.year() > moment().year()) {
					birth_date.subtract(2000, "year");
				}

				setFieldValue("tutor.birth_date", birth_date.format("YYYY-MM-DD"));
			}
		}, 500),
		[]
	);

	/* 	useEffect(() => {
		console.log("useEffect values", values);
		if (values?.tutor?.relation?.length > 0) {
			setRelation(values.tutor.relation);
			setTutor(values.tutor);
		}
	}, [values]); */

	const updateFormWithGranteeData = (foundGrantee, setFieldValue) => {
		console.log("updateFormWithGranteeData", foundGrantee);
		setFieldValue("tutor.mylabId", foundGrantee.id || foundGrantee.mylabId || 0);
		setFieldValue("tutor.birth_name", foundGrantee.birth_name || "");
		setFieldValue("tutor.first_name", foundGrantee.first_name || "");
		setFieldValue("tutor.married_name", foundGrantee.marital_name || "");
		setFieldValue("tutor.birth_date", foundGrantee.birth_date || "");
		setFieldValue("tutor.mobile_phone", foundGrantee.mobile_phone || foundGrantee.phone || "");
		setFieldValue("tutor.email", foundGrantee.email || "");
		setFieldValue("tutor.cns", foundGrantee.matricule || foundGrantee.cns || "");
		setFieldValue("tutor.sex", foundGrantee.gender === "m" || foundGrantee.gender === "male" ? "male" : "female");
		setFieldValue(
			"tutor.specialRegime.id",
			foundGrantee.specialRegime?.id || foundGrantee.health_fund_id ? findSpecialRegime(foundGrantee.health_fund_id) : null || null
		);
	};

	return (
		<Form>
			<Grid>
				<Grid.Row>
					<Grid.Column mobile={13} tablet={13} computer={13}>
						<Field type="number" name="mylabId" hidden />
						<Grid.Column mobile={16} tablet={16} computer={16}>
							<Field
								component={Select}
								name="tutor.specialRegime.id"
								customlabel="Régime"
								placeholder="Sélectionner un régime"
								options={specialRegimeOptions}
								disabled={formIsDisabled}
								required
							/>
						</Grid.Column>
						<Field
							type="text"
							name="tutor.cns"
							customlabel="N° CNS"
							placeholder=""
							component={CustomInputTextComponent}
							disabled={formIsDisabled}
							errors={errors}
							required
							onKeyUp={(e) => {
								const val = e.target.value;
								handleKeyUp(val, setFieldValue);
							}}
						/>
					</Grid.Column>
					<Grid.Column
						mobile={3}
						tablet={3}
						computer={3}
						style={{
							display: "flex",
							justifyContent: "flex-end",
							alignItems: "flex-end",
						}}
					>
						<Popup
							content="Rechercher les données du patient"
							position="top right"
							trigger={
								<Button
									color="green"
									type="button"
									icon="search"
									disabled={!new RegExp("^[0-9]{13}$").test(values.tutor.cns)}
									onClick={(e) => {
										if (e !== undefined) e.preventDefault();
										searchByMatricule(values.tutor.cns);
									}}
								/>
							}
						/>
					</Grid.Column>
					{foundGrantee && (
						<GranteeSummary
							foundGrantee={foundGrantee}
							onCancel={() => initFoundGrantee()}
							onUse={() => {
								updateFormWithGranteeData(foundGrantee, setFieldValue);
								initFoundGrantee();
							}}
						/>
					)}
				</Grid.Row>
			</Grid>

			<Field
				type="text"
				name="tutor.birth_name"
				customlabel="Nom de naissance"
				placeholder=""
				component={CustomInputTextComponent}
				required
				forceUpperCase
				errors={errors}
				disabled={formIsDisabled}
			/>

			<Field
				type="text"
				name="tutor.first_name"
				customlabel="Prénom(s)"
				placeholder=""
				component={CustomInputTextComponent}
				required
				forceCapitalize
				errors={errors}
				disabled={formIsDisabled}
			/>

			<Field
				type="text"
				name="tutor.married_name"
				customlabel="Nom marital"
				placeholder=""
				component={CustomInputTextComponent}
				forceUpperCase
				disabled={formIsDisabled}
			/>

			<Field name="tutor.birth_date" customlabel="Date de naissance" component={CustomDatePickerComponent} disabled={formIsDisabled} required />
			<RadioButtons
				name="tutor.sex"
				field1={{ id: "male", customlabel: "Homme" }}
				field2={{ id: "female", customlabel: "Femme" }}
				customlabel="Genre"
				disabled={formIsDisabled}
				errors={errors}
				required
			/>

			<Field type="text" name="tutor.email" placeholder="" customlabel="Adresse email" component={CustomInputTextComponent} />
			<Field component={InputPhone} name="tutor.mobile_phone" placeholder="" customlabel="Téléphone mobile" />

			<Field
				component={Select}
				isCreatable
				name="tutor.relation"
				customlabel="Relation"
				options={contactComments.list.map((reason) => {
					return {
						value: reason.label,
						label: reason.label,
					};
				})}
				placeholder="Sélectionner une relation"
				errors={errors}
				required
			/>
		</Form>
	);
};

export default GranteeForm;
